import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { getApiUrl } from '../config/development';
import AsyncStorage from '@react-native-async-storage/async-storage';

const SocketContext = createContext({
    socket: null,
    connected: false,
    onlineUsers: new Set(),
});

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
    const { user, isAuthenticated } = useAuth();
    const [socket, setSocket] = useState(null);
    const [connected, setConnected] = useState(false);
    const [onlineUsers, setOnlineUsers] = useState(new Set());
    const socketRef = useRef(null);

    useEffect(() => {
        if (isAuthenticated && user) {
            connectSocket();
        } else {
            disconnectSocket();
        }

        return () => disconnectSocket();
    }, [isAuthenticated, user?.id]);

    const connectSocket = async () => {
        if (socketRef.current) return;

        try {
            const token = await AsyncStorage.getItem('token');
            const apiUrl = getApiUrl();
            const baseUrl = apiUrl.replace('/api', '');

            console.log('Connecting to socket at:', baseUrl);

            const newSocket = io(baseUrl, {
                auth: { token },
                path: '/api/socket.io',
                transports: ['websocket', 'polling'],
                upgrade: true,
                rememberUpgrade: true,
                reconnection: true,
                reconnectionAttempts: 10,
                reconnectionDelay: 1000,
                timeout: 30000,
                forceNew: true
            });



            newSocket.on('connect', () => {
                const transport = newSocket.io.engine.transport.name;
                console.log(`Socket connected: ${newSocket.id} via ${transport}`);
                setConnected(true);
            });

            newSocket.on('disconnect', (reason) => {
                console.log('Socket disconnected:', reason);
                setConnected(false);
            });

            newSocket.on('connect_error', (error) => {
                console.error('Socket connection error:', error);
                setConnected(false);
            });

            newSocket.on('user_online', ({ userId }) => {
                setOnlineUsers(prev => new Set(prev).add(userId));
            });

            newSocket.on('user_offline', ({ userId }) => {
                setOnlineUsers(prev => {
                    const next = new Set(prev);
                    next.delete(userId);
                    return next;
                });
            });

            socketRef.current = newSocket;
            setSocket(newSocket);
        } catch (error) {
            console.error('Failed to connect socket:', error);
        }
    };

    const disconnectSocket = () => {
        if (socketRef.current) {
            console.log('Disconnecting socket');
            socketRef.current.disconnect();
            socketRef.current = null;
            setSocket(null);
            setConnected(false);
            setOnlineUsers(new Set());
        }
    };

    return (
        <SocketContext.Provider value={{ socket, connected, onlineUsers }}>
            {children}
        </SocketContext.Provider>
    );
};
